"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { apiDelete, apiGet, apiPost } from "@/lib/api";
import { ALL_ALBUM_ID, useAlbums } from "@/lib/album-context";
import { useToast } from "@/components/toast-provider";
import { RequireAuth } from "@/components/require-auth";
import ConfirmDialog from "@/components/confirm-dialog";
import SharePhotosModal from "@/components/share-photos-modal";
import * as s from "@/lib/styles";

export type Photo = {
  id: string;
  ownerId: number;
  uploadedBy: string;
  originalName: string | null;
  createdAt: string;
  takenAt: string | null;
  width: number | null;
  height: number | null;
  latitude: number | null;
  longitude: number | null;
  locationLabel: string | null;
  cameraMake: string | null;
  cameraModel: string | null;
  caption: string | null;
  hasThumbnail: boolean;
  albumId: number;
  albumName: string;
  mediaKind: "photo" | "video" | "live_photo";
  durationSecs: number | null;
  hasVideo: boolean;
  mine: boolean;
  sharedIn: boolean;
};

function duration(secs: number | null): string {
  if (!secs || secs <= 0) return "";
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

type Person = { id: number; name: string; photoCount: number };

type Filters = {
  from: string;
  to: string;
  hasLocation: boolean;
  camera: string;
  q: string;
  personId: string;
};

const EMPTY_FILTERS: Filters = {
  from: "",
  to: "",
  hasLocation: false,
  camera: "",
  q: "",
  personId: "",
};

function buildQuery(albumId: number, f: Filters): string {
  const p = new URLSearchParams();
  p.set("albumId", String(albumId));
  if (f.from) p.set("from", f.from);
  if (f.to) p.set("to", f.to);
  if (f.hasLocation) p.set("hasLocation", "true");
  if (f.camera.trim()) p.set("camera", f.camera.trim());
  if (f.q.trim()) p.set("q", f.q.trim());
  if (f.personId) p.set("personId", f.personId);
  return p.toString();
}

/** Group photos by the month they were taken — the usual way to skim a library. */
function groupByMonth(photos: Photo[]): [string, Photo[]][] {
  const groups = new Map<string, Photo[]>();
  for (const p of photos) {
    const when = p.takenAt ?? p.createdAt;
    const label = new Date(when).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
    });
    const list = groups.get(label);
    if (list) list.push(p);
    else groups.set(label, [p]);
  }
  return [...groups.entries()];
}

function PhotosPage() {
  const { showToast } = useToast();
  const { current, currentAlbum, refresh: refreshAlbums } = useAlbums();

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Photo | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const canUpload = Boolean(currentAlbum?.canContribute);
  const filtersActive = useMemo(
    () => JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS),
    [filters],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet<Photo[]>(`/photos?${buildQuery(current, filters)}`);
      setPhotos(data);
      // Drop selections for photos no longer on screen.
      setSelected((prev) => {
        const live = new Set(data.map((p) => p.id));
        return new Set([...prev].filter((id) => live.has(id)));
      });
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Could not load photos");
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, [current, filters, showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    apiGet<Person[]>("/people")
      .then(setPeople)
      .catch(() => setPeople([]));
  }, []);

  type UploadItemResult = {
    originalName: string;
    stored: boolean;
    duplicateOf?: string;
    error?: string;
    warning?: string;
    mediaKind?: string;
  };

  function explainHttpStatus(status: number, serverMsg?: string): string {
    if (serverMsg) return serverMsg;
    if (status === 413) {
      return "File is too large (server limit is 1 GB). Export a smaller copy or split the batch.";
    }
    if (status === 401 || status === 403) {
      return "You are not signed in, or you cannot add photos to this album. Sign in again and pick an album you own or can contribute to.";
    }
    if (status === 400) {
      return "The upload was incomplete or not a supported photo/video. For Photos videos, export the original or drag from Finder.";
    }
    if (status === 502 || status === 504) {
      return "The server timed out while receiving the upload. Try a smaller file or a better network connection.";
    }
    if (status >= 500) {
      return "Something went wrong on the server. Try again; if it keeps failing, check the backend logs.";
    }
    return `Upload failed (HTTP ${status}).`;
  }

  function reportUploadResults(
    data: { uploaded: number; message?: string; items: UploadItemResult[] },
    clientFailures: string[],
  ) {
    const failed = data.items.filter((i) => i.error);
    const warned = data.items.filter((i) => i.warning);
    const dupes = data.items.filter((i) => i.duplicateOf).length;
    const videos = data.items.filter(
      (i) => i.stored && (i.mediaKind === "video" || i.mediaKind === "live_photo"),
    ).length;

    // Console trail for anyone debugging in Safari Web Inspector.
    console.info("[photos upload]", {
      uploaded: data.uploaded,
      message: data.message,
      failed: failed.map((f) => ({ name: f.originalName, error: f.error })),
      warnings: warned.map((w) => ({ name: w.originalName, warning: w.warning })),
      clientFailures,
    });

    const summary =
      data.message ||
      (() => {
        let msg = `Uploaded ${data.uploaded} item${data.uploaded === 1 ? "" : "s"}`;
        if (videos) msg += ` (${videos} video${videos === 1 ? "" : "s"})`;
        if (dupes) msg += ` · skipped ${dupes} duplicate${dupes === 1 ? "" : "s"}`;
        if (failed.length) msg += ` · ${failed.length} failed`;
        if (warned.length) msg += ` · ${warned.length} with warnings`;
        return msg;
      })();

    if (failed.length === 0 && clientFailures.length === 0 && warned.length === 0) {
      showToast("success", summary);
    } else if (data.uploaded > 0) {
      showToast(failed.length || clientFailures.length ? "warning" : "success", summary);
    } else {
      showToast("error", summary);
    }

    for (const f of failed) {
      showToast("error", `${f.originalName}: ${f.error}`);
    }
    for (const w of warned) {
      showToast("warning", `${w.originalName}: ${w.warning}`);
    }
    for (const c of clientFailures) {
      showToast("error", c);
    }
  }

  async function onUpload(files: FileList | File[] | null) {
    if (!files || files.length === 0) return;
    if (current === ALL_ALBUM_ID) {
      showToast("error", "Pick an album first — “All photos” is read-only.");
      return;
    }
    if (!canUpload) {
      showToast(
        "error",
        "You cannot add photos to this album. Choose an album you own, or one shared with contribute access.",
      );
      return;
    }
    setUploading(true);
    const clientFailures: string[] = [];
    try {
      // Photos (and other file-promise sources) hand the browser a path that
      // may still be exporting. Read every byte into a stable File before the
      // request starts — otherwise Safari aborts mid-multipart for videos and
      // the server sees a truncated body (HTTP 400, empty response).
      const ready: File[] = [];
      for (const raw of Array.from(files)) {
        const label = raw.name || "file";
        try {
          ready.push(await materializeFile(raw));
        } catch (err) {
          const why = err instanceof Error ? err.message : "could not read";
          const msg = `${label}: ${why}`;
          console.warn("[photos upload] client read failed", label, err);
          clientFailures.push(msg);
        }
      }
      if (ready.length === 0) {
        console.warn("[photos upload] nothing readable", { clientFailures });
        if (clientFailures.length > 0) {
          showToast(
            "error",
            `Could not read ${clientFailures.length} file${clientFailures.length === 1 ? "" : "s"} from Photos/Finder.`,
          );
          for (const c of clientFailures) showToast("error", c);
        } else {
          showToast(
            "error",
            "Nothing to upload — the drop had no readable files. If this was a video from Photos, export it (File → Export → Export Unmodified Original) or drag from Finder instead.",
          );
        }
        return;
      }

      const totalBytes = ready.reduce((n, f) => n + f.size, 0);
      console.info("[photos upload] sending", {
        count: ready.length,
        totalMB: (totalBytes / (1024 * 1024)).toFixed(1),
        names: ready.map((f) => f.name),
        albumId: current,
      });

      const form = new FormData();
      form.append("albumId", String(current));
      for (const f of ready) form.append("files", f);
      const res = await fetch("/api/photos", {
        method: "POST",
        credentials: "same-origin",
        body: form,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({} as { error?: string }));
        const msg = explainHttpStatus(res.status, body.error);
        console.error("[photos upload] HTTP error", res.status, body);
        throw new Error(msg);
      }
      const data = (await res.json()) as {
        uploaded: number;
        message?: string;
        ok?: boolean;
        items: UploadItemResult[];
      };
      reportUploadResults(data, clientFailures);
      await load();
      await refreshAlbums();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Upload failed";
      console.error("[photos upload] exception", e);
      // Network failures (offline, VPN drop) have no HTTP body.
      if (e instanceof TypeError) {
        showToast(
          "error",
          "Could not reach the server. Check your VPN/network and try again.",
        );
      } else {
        showToast("error", msg);
      }
      for (const c of clientFailures) showToast("error", c);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  /**
   * Dropping from the macOS Photos app: unlike Finder, Photos hands the browser
   * a *promised* file. Several browsers expose that only through
   * `dataTransfer.items` (`getAsFile()` / `webkitGetAsEntry`) and leave
   * `dataTransfer.files` empty — so reading `.files` alone drops Photos drags
   * on the floor. Videos are worse: the promise may still be exporting when
   * the drop event fires, so we resolve entries asynchronously and fully
   * materialize bytes before POSTing. A Live Photo arrives as a HEIC + MOV
   * pair; the server rejoins them by filename stem, so both must go up in the
   * SAME request.
   */
  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    void (async () => {
      const files = await collectDroppedFiles(e.dataTransfer);
      if (files.length === 0) {
        showToast(
          "error",
          "That drop had no files. Videos still in iCloud need to be downloaded in Photos first, or export them to Finder and drag from there.",
        );
        return;
      }
      await onUpload(files);
    })();
  }

  /**
   * Force a Photos/Finder file promise into a real in-memory File. Empty or
   * unreadable sources throw so the caller can surface a useful toast.
   */
  async function materializeFile(file: File): Promise<File> {
    const name = file.name || guessDroppedName(file);
    // 1 GiB client-side guard — matches nginx/axum. Real phone videos can be
    // large; fail loudly rather than OOM the tab.
    const maxBytes = 1024 * 1024 * 1024;
    if (file.size > maxBytes) {
      throw new Error(
        `too large (${(file.size / (1024 * 1024)).toFixed(0)} MB; server max is 1024 MB). Export a smaller version or upload fewer items at once.`,
      );
    }
    // size === 0 often means a Photos file promise that has not finished
    // writing yet (or an iCloud placeholder). Still try to read — sometimes
    // arrayBuffer resolves the real bytes even when size was reported as 0.
    let buf: ArrayBuffer;
    try {
      buf = await file.arrayBuffer();
    } catch (err) {
      console.warn("[photos upload] arrayBuffer failed", name, err);
      throw new Error(
        "could not read bytes — still in iCloud, or Photos finished exporting too late. Open the item in Photos to download the original, then File → Export → Export Unmodified Original.",
      );
    }
    if (buf.byteLength === 0) {
      throw new Error(
        "empty file — Photos may not have finished exporting. Wait a moment and try again, or export the unmodified original from Photos to Finder first.",
      );
    }
    const type =
      file.type ||
      (isVideoName(name) ? "video/quicktime" : isImageName(name) ? "image/jpeg" : "");
    return new File([buf], name, {
      type,
      lastModified: file.lastModified || Date.now(),
    });
  }

  function guessDroppedName(file: File): string {
    if (file.type.startsWith("video/")) return "video.mov";
    if (file.type === "image/heic" || file.type === "image/heif") return "photo.heic";
    if (file.type.startsWith("image/")) return "photo.jpg";
    return "upload.bin";
  }

  function isVideoName(name: string): boolean {
    return /\.(mov|mp4|m4v|avi|mkv|hevc)$/i.test(name);
  }

  function isImageName(name: string): boolean {
    return /\.(jpe?g|png|gif|webp|bmp|tiff?|heic|heif)$/i.test(name);
  }

  /**
   * Merge every place a drop can surface files. Finder fills
   * `dataTransfer.files`; macOS Photos often only fills `items`, and for
   * videos `getAsFile()` is sometimes null until `webkitGetAsEntry().file()`
   * resolves. De-dupe when both expose the same entry.
   */
  async function collectDroppedFiles(dt: DataTransfer | null): Promise<File[]> {
    if (!dt) return [];
    const out: File[] = [];
    const seen = new Set<string>();
    const add = (f: File | null | undefined) => {
      if (!f) return;
      const key = `${f.name}:${f.size}:${f.lastModified}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push(f);
    };

    const entryJobs: Promise<void>[] = [];
    if (dt.items) {
      for (const item of Array.from(dt.items)) {
        if (item.kind !== "file") continue;
        const direct = item.getAsFile();
        if (direct) {
          add(direct);
          continue;
        }
        // Promised / delayed files (common for Photos videos).
        const entry = item.webkitGetAsEntry?.();
        if (entry && entry.isFile) {
          entryJobs.push(
            new Promise<void>((resolve) => {
              (entry as FileSystemFileEntry).file(
                (f) => {
                  add(f);
                  resolve();
                },
                () => resolve(),
              );
            }),
          );
        }
      }
    }
    await Promise.all(entryJobs);
    if (dt.files) for (const f of Array.from(dt.files)) add(f);
    return out;
  }

  function onDragOver(e: React.DragEvent) {
    // Without preventDefault the browser navigates to the dropped file.
    e.preventDefault();
    if (!dragging) setDragging(true);
  }

  function onDragLeave(e: React.DragEvent) {
    // Only clear when the pointer leaves the drop surface itself, not when it
    // crosses between child tiles.
    if (e.currentTarget === e.target) setDragging(false);
  }

  // Cmd-V from Photos or Finder pastes images straight in.
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      const files = e.clipboardData?.files;
      if (files && files.length) void onUpload(files);
    }
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  });

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function doDelete(photo: Photo) {
    try {
      await apiDelete(`/photos/${photo.id}`);
      showToast("success", "Photo deleted");
      await load();
      await refreshAlbums();
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Delete failed");
    } finally {
      setConfirmDelete(null);
    }
  }

  /** Remove a shared-in photo from this album (does not delete the photo). */
  async function doUnshare(photo: Photo) {
    try {
      await apiDelete(`/photos/${photo.id}/albums/${current}`);
      showToast("success", "Removed from this album");
      await load();
      await refreshAlbums();
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Could not remove");
    }
  }

  async function shareWholeAlbum(targetAlbumId: number) {
    try {
      const res = await apiPost<{ shared: number; skipped: number }>(
        `/albums/${current}/share-all`,
        { targetAlbumId },
      );
      showToast(
        "success",
        `Shared ${res.shared} photo${res.shared === 1 ? "" : "s"}` +
          (res.skipped ? `, skipped ${res.skipped} already there` : ""),
      );
      await refreshAlbums();
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Share failed");
    }
  }

  const selectedMine = photos.filter((p) => selected.has(p.id) && p.mine);
  const grouped = groupByMonth(photos);

  return (
    <div
      className="p-6 space-y-5 relative min-h-[70vh]"
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      {dragging && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 pointer-events-none">
          <div className="rounded-xl border-2 border-dashed border-accent bg-surface px-8 py-6 text-center">
            <p className="text-base font-semibold text-foreground m-0">
              Drop to upload
            </p>
            <p className="text-xs text-muted m-0 mt-1">
              {canUpload
                ? `Photos and videos will be added to ${currentAlbum?.name ?? "this album"}`
                : "Choose an album you can add to first"}
            </p>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className={s.pageTitle + " mb-0"}>
          {currentAlbum?.name ?? "Photos"}
          {currentAlbum && !currentAlbum.owned && currentAlbum.owner && (
            <span className="ml-2 text-sm font-normal text-muted">
              shared by {currentAlbum.owner}
            </span>
          )}
        </h1>

        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={fileInput}
            type="file"
            accept="image/*,video/*,.heic,.heif,.mov,.mp4,.m4v"
            multiple
            hidden
            onChange={(e) => void onUpload(e.target.files)}
          />
          <button
            type="button"
            className={s.btnPrimary}
            disabled={!canUpload || uploading}
            title={
              canUpload
                ? "Upload photos into this album"
                : current === ALL_ALBUM_ID
                  ? "Pick an album to upload into"
                  : "You do not have permission to add photos here"
            }
            onClick={() => fileInput.current?.click()}
          >
            {uploading ? "Uploading…" : "Upload photos"}
          </button>
          <button
            type="button"
            className={s.btnAuxiliary}
            disabled={selectedMine.length === 0}
            title={
              selectedMine.length
                ? "Share the selected photos into another album"
                : "Select photos you own to share them"
            }
            onClick={() => setShareOpen(true)}
          >
            Share selected ({selectedMine.length})
          </button>
        </div>
      </div>

      {/* Smart-album filters. Each maps to a backend query parameter. */}
      <div className="rounded-lg border border-border bg-surface p-4 space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          <label className="flex flex-col gap-1 text-xs text-muted">
            Taken from
            <input
              type="date"
              className={s.input}
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted">
            to
            <input
              type="date"
              className={s.input}
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted">
            Camera
            <input
              className={s.input}
              placeholder="any"
              value={filters.camera}
              onChange={(e) => setFilters({ ...filters, camera: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted">
            Person
            <select
              className={s.input}
              value={filters.personId}
              onChange={(e) => setFilters({ ...filters, personId: e.target.value })}
            >
              <option value="">anyone</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.photoCount})
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted">
            Search
            <input
              className={s.input}
              placeholder="name, caption, place"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-muted pb-2">
            <input
              type="checkbox"
              checked={filters.hasLocation}
              onChange={(e) => setFilters({ ...filters, hasLocation: e.target.checked })}
            />
            Has location
          </label>
          {filtersActive && (
            <button
              type="button"
              className="text-xs text-muted hover:text-accent pb-2 underline"
              onClick={() => setFilters(EMPTY_FILTERS)}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {currentAlbum?.owned && current !== ALL_ALBUM_ID && (
        <ShareWholeAlbumBar onShare={shareWholeAlbum} />
      )}

      {loading ? (
        <p className="text-sm text-muted">Loading photos…</p>
      ) : photos.length === 0 ? (
        <p className="text-sm text-muted">
          {filtersActive
            ? "No photos match those filters."
            : canUpload
              ? "No photos here yet — drag them in from Photos, or use Upload."
              : "No photos here yet."}
        </p>
      ) : (
        grouped.map(([month, group]) => (
          <section key={month} className="space-y-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted m-0">
              {month} <span className="text-muted/70">· {group.length}</span>
            </h2>
            <ul className="list-none p-0 m-0 grid gap-3 grid-cols-[repeat(auto-fill,minmax(160px,1fr))]">
              {group.map((photo) => {
                const isSelected = selected.has(photo.id);
                return (
                  <li
                    key={photo.id}
                    className={`relative group rounded-lg overflow-hidden border transition-colors ${
                      isSelected ? "border-accent" : "border-border hover:border-accent/60"
                    }`}
                  >
                    <Link href={`/photos/${photo.id}`} className="block relative">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={`/api/photos/${photo.id}/thumb`}
                        alt={photo.caption || photo.originalName || "Photo"}
                        loading="lazy"
                        className="w-full h-40 object-cover bg-surface-light"
                      />
                      {/* A Live Photo plays its motion clip on hover, like Photos.app. */}
                      {photo.mediaKind === "live_photo" && (
                        <video
                          src={`/api/photos/${photo.id}/video`}
                          muted
                          playsInline
                          preload="none"
                          className="absolute inset-0 w-full h-40 object-cover opacity-0 hover:opacity-100 transition-opacity"
                          onMouseEnter={(e) => void e.currentTarget.play().catch(() => {})}
                          onMouseLeave={(e) => {
                            e.currentTarget.pause();
                            e.currentTarget.currentTime = 0;
                          }}
                        />
                      )}
                      {photo.mediaKind === "video" && (
                        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center text-white text-sm">
                            ▶
                          </span>
                        </span>
                      )}
                      {photo.mediaKind === "live_photo" && (
                        <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px] font-semibold tracking-wide pointer-events-none">
                          LIVE
                        </span>
                      )}
                      {photo.durationSecs != null && photo.mediaKind === "video" && (
                        <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] tabular-nums pointer-events-none">
                          {duration(photo.durationSecs)}
                        </span>
                      )}
                    </Link>

                    <div className="px-2 py-1.5 bg-surface">
                      <div className="text-[11px] text-foreground truncate">
                        {photo.caption || photo.originalName || "Untitled"}
                      </div>
                      <div className="text-[10px] text-muted truncate">
                        {(photo.takenAt ?? photo.createdAt).slice(0, 10)}
                        {photo.locationLabel
                          ? ` · 📍 ${photo.locationLabel}`
                          : photo.latitude != null
                            ? " · 📍"
                            : ""}
                        {!photo.mine && ` · ${photo.uploadedBy}`}
                        {photo.sharedIn && " · shared in"}
                      </div>
                    </div>

                    {/* Only the owner is offered destructive actions. */}
                    <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {photo.mine && (
                        <button
                          type="button"
                          title="Delete this photo everywhere"
                          className="w-6 h-6 rounded bg-black/70 text-white text-xs hover:bg-red"
                          onClick={() => setConfirmDelete(photo)}
                        >
                          ✕
                        </button>
                      )}
                      {photo.sharedIn && (photo.mine || currentAlbum?.owned) && (
                        <button
                          type="button"
                          title="Remove from this album (keeps the photo)"
                          className="w-6 h-6 rounded bg-black/70 text-white text-xs hover:bg-accent"
                          onClick={() => void doUnshare(photo)}
                        >
                          ⊘
                        </button>
                      )}
                    </div>

                    {photo.mine && (
                      <label
                        className="absolute bottom-1 left-1 flex items-center justify-center w-6 h-6 rounded bg-black/70 cursor-pointer"
                        title="Select for sharing"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(photo.id)}
                        />
                      </label>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}

      <SharePhotosModal
        open={shareOpen}
        photoIds={selectedMine.map((p) => p.id)}
        onClose={() => setShareOpen(false)}
        onShared={() => {
          setSelected(new Set());
          void load();
          void refreshAlbums();
        }}
      />

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete photo?"
        message={
          confirmDelete
            ? `“${confirmDelete.caption || confirmDelete.originalName || "This photo"}” will be permanently deleted, including from any album it was shared into.`
            : ""
        }
        confirmLabel="Delete"
        onConfirm={() => confirmDelete && void doDelete(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

/** Bulk-share every photo you own in this album into another album. */
function ShareWholeAlbumBar({ onShare }: { onShare: (targetId: number) => void }) {
  const { own, shared } = useAlbums();
  const { current } = useAlbums();
  const [target, setTarget] = useState("");
  const targets = [...own, ...shared].filter(
    (a) => a.id !== current && a.canContribute,
  );
  if (targets.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap text-xs text-muted">
      <span>Share this album&rsquo;s contents into:</span>
      <select
        className={s.input + " max-w-xs"}
        value={target}
        onChange={(e) => setTarget(e.target.value)}
      >
        <option value="">choose an album…</option>
        {targets.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
            {!a.owned ? ` (${a.owner})` : ""}
          </option>
        ))}
      </select>
      <button
        type="button"
        className={s.btnAuxiliary + " !py-1 !px-3"}
        disabled={!target}
        onClick={() => {
          onShare(Number(target));
          setTarget("");
        }}
      >
        Share all
      </button>
    </div>
  );
}

export default function Page() {
  return (
    <RequireAuth>
      <PhotosPage />
    </RequireAuth>
  );
}
